// Decoders for the entity trees produced by web-ifc's GetLine
// (flatten=true). Every numeric scalar from the IFC step file comes
// back as { type: <ifcMeasureCode>, value: number }; every entity
// reference is replaced inline with the resolved entity. These helpers
// peel that off into shapes the inspector UI can render directly.

type Anyish = Record<string, unknown> | null | undefined

function asObj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null
}

function getValue(v: unknown): unknown {
  if (v === null || v === undefined) return null
  if (typeof v !== 'object') return v
  const o = v as Record<string, unknown>
  if ('value' in o) return o.value
  return v
}

function getNumber(v: unknown): number | null {
  const x = getValue(v)
  return typeof x === 'number' && Number.isFinite(x) ? x : null
}

function getString(v: unknown): string | null {
  const x = getValue(v)
  return typeof x === 'string' && x.length > 0 ? x : null
}

function getNumberArray(v: unknown): number[] | null {
  if (!Array.isArray(v)) return null
  const out: number[] = []
  for (const e of v) {
    const n = getNumber(e)
    if (n === null) return null
    out.push(n)
  }
  return out.length > 0 ? out : null
}

interface PlacementEntry {
  type: string
  expressID: number | null
  location: number[] | null
  axisZ: number[] | null
  axisX: number[] | null
  rotationDeg: number | null
}

// Compute the accumulated world-frame origin for each level of the
// placement chain. Convention: chain[0] is the element itself,
// chain[len-1] is the outermost (closest to IfcSite). World M for the
// element = chain[len-1].M * ... * chain[0].M (parent-first
// composition, IFC semantics).
import * as THREE from 'three'

function placementMatrix(p: PlacementEntry): THREE.Matrix4 {
  const loc = p.location ?? [0, 0, 0]
  const zArr = p.axisZ ?? [0, 0, 1]
  const xArr = p.axisX ?? [1, 0, 0]
  const Z = new THREE.Vector3(zArr[0], zArr[1], zArr[2]).normalize()
  const Xraw = new THREE.Vector3(xArr[0], xArr[1], xArr[2])
  const Y = new THREE.Vector3().crossVectors(Z, Xraw).normalize()
  const X = new THREE.Vector3().crossVectors(Y, Z).normalize()
  const m = new THREE.Matrix4().makeBasis(X, Y, Z)
  m.setPosition(loc[0], loc[1], loc[2])
  return m
}

export function accumulateChain(
  chain: PlacementEntry[],
): { x: number; y: number; z: number }[] {
  const out: { x: number; y: number; z: number }[] = []
  for (let i = 0; i < chain.length; i++) {
    const m = new THREE.Matrix4()
    for (let j = chain.length - 1; j >= i; j--) {
      m.multiply(placementMatrix(chain[j]))
    }
    const v = new THREE.Vector3().setFromMatrixPosition(m)
    out.push({ x: v.x, y: v.y, z: v.z })
  }
  return out
}

export function decodePlacement(raw: Anyish): PlacementEntry | null {
  const o = asObj(raw)
  if (!o) return null
  const type = getString(o.type) ?? '?'
  const expressID = typeof o.expressID === 'number' ? o.expressID : null
  const rel = asObj(o.RelativePlacement)
  if (!rel) {
    return { type, expressID, location: null, axisZ: null, axisX: null, rotationDeg: null }
  }
  const loc = asObj(rel.Location)
  const axis = asObj(rel.Axis)
  const ref = asObj(rel.RefDirection)
  const location = loc ? getNumberArray(loc.Coordinates) : null
  const axisZ = axis ? getNumberArray(axis.DirectionRatios) : null
  const axisX = ref ? getNumberArray(ref.DirectionRatios) : null
  let rotationDeg: number | null = null
  if (axisX && axisX.length >= 2) {
    rotationDeg = (Math.atan2(axisX[1], axisX[0]) * 180) / Math.PI
  }
  return { type, expressID, location, axisZ, axisX, rotationDeg }
}

interface RepresentationItemSummary {
  type: string
  expressID: number | null
  detail: string
  isMapped: boolean
  // For MappedItem only.
  mappingSourceID?: number | null
  mappedRepresentationType?: string | null
  mappedRepresentationItemCount?: number | null
}

interface RepresentationEntry {
  identifier: string
  type: string
  contextType: string | null
  items: RepresentationItemSummary[]
}

export function decodeRepresentation(raw: Anyish): RepresentationEntry[] {
  const o = asObj(raw)
  if (!o) return []
  // raw is the IfcWall entity: o.Representation is IfcProductDefinitionShape.
  const shape = asObj(o.Representation)
  if (!shape) return []
  const reps = Array.isArray(shape.Representations)
    ? shape.Representations
    : []
  const out: RepresentationEntry[] = []
  for (const r of reps) {
    const ro = asObj(r)
    if (!ro) continue
    const ctx = asObj(ro.ContextOfItems)
    const ctxType = ctx ? getString(ctx.ContextType) : null
    const items: RepresentationItemSummary[] = []
    const itemList = Array.isArray(ro.Items) ? ro.Items : []
    for (const it of itemList) items.push(decodeRepresentationItem(it))
    out.push({
      identifier: getString(ro.RepresentationIdentifier) ?? '',
      type: getString(ro.RepresentationType) ?? '?',
      contextType: ctxType,
      items,
    })
  }
  return out
}

function decodeRepresentationItem(raw: unknown): RepresentationItemSummary {
  const o = asObj(raw)
  const type = (o ? getString(o.type) : null) ?? '?'
  const expressID =
    o && typeof o.expressID === 'number' ? (o.expressID as number) : null
  const u = type.toUpperCase()
  const isMapped = u === 'IFCMAPPEDITEM' || u.includes('MAPPED')

  if (!o) return { type, expressID, detail: '', isMapped }

  if (u === 'IFCEXTRUDEDAREASOLID') {
    const profile = asObj(o.SweptArea)
    const profileType = profile ? getString(profile.type) : null
    const profileName = profile ? getString(profile.ProfileName) : null
    const depth = getNumber(o.Depth)
    const dirObj = asObj(o.ExtrudedDirection)
    const dir = dirObj ? getNumberArray(dirObj.DirectionRatios) : null
    const detail = [
      profileType
        ? `profile=${profileType}${profileName ? ` "${profileName}"` : ''}`
        : null,
      depth !== null ? `depth=${depth}` : null,
      dir ? `dir=[${dir.map((n) => n.toFixed(3)).join(', ')}]` : null,
    ]
      .filter(Boolean)
      .join(' · ')
    return { type, expressID, detail, isMapped }
  }

  if (u === 'IFCMAPPEDITEM') {
    const src = asObj(o.MappingSource)
    let sourceID: number | null = null
    let mappedRepType: string | null = null
    let mappedItems: number | null = null
    if (src) {
      sourceID =
        typeof src.expressID === 'number' ? (src.expressID as number) : null
      const mappedRep = asObj(src.MappedRepresentation)
      if (mappedRep) {
        mappedRepType = getString(mappedRep.RepresentationType)
        const items = Array.isArray(mappedRep.Items) ? mappedRep.Items : []
        mappedItems = items.length
      }
    }
    const detail = [
      sourceID !== null ? `RepresentationMap #${sourceID}` : null,
      mappedRepType ? `→ ${mappedRepType}` : null,
      mappedItems !== null ? `${mappedItems} item(s)` : null,
    ]
      .filter(Boolean)
      .join(' · ')
    return {
      type,
      expressID,
      detail,
      isMapped: true,
      mappingSourceID: sourceID,
      mappedRepresentationType: mappedRepType,
      mappedRepresentationItemCount: mappedItems,
    }
  }

  if (u === 'IFCFACETEDBREP' || u === 'IFCADVANCEDBREP') {
    const outer = asObj(o.Outer)
    const faces = outer && Array.isArray(outer.CfsFaces) ? outer.CfsFaces.length : 0
    return { type, expressID, detail: `${faces} faces`, isMapped }
  }

  if (
    u === 'IFCTRIANGULATEDFACESET' ||
    u === 'IFCPOLYGONALFACESET' ||
    u === 'IFCTESSELLATEDFACESET'
  ) {
    const coords = asObj(o.Coordinates)
    const coordList =
      coords && Array.isArray(coords.CoordList) ? coords.CoordList : []
    const indices = Array.isArray(o.CoordIndex)
      ? o.CoordIndex
      : Array.isArray(o.Faces)
        ? o.Faces
        : []
    return {
      type,
      expressID,
      detail: `${coordList.length} vértices, ${indices.length} índices`,
      isMapped,
    }
  }

  if (u === 'IFCBOOLEANRESULT' || u === 'IFCBOOLEANCLIPPINGRESULT') {
    const op = getString(o.Operator)
    return { type, expressID, detail: op ? `op=${op}` : '', isMapped }
  }

  if (u === 'IFCBOUNDINGBOX') {
    const dims = [getNumber(o.XDim), getNumber(o.YDim), getNumber(o.ZDim)]
    if (dims.every((d) => d !== null)) {
      return {
        type,
        expressID,
        detail: `${dims[0]} × ${dims[1]} × ${dims[2]}`,
        isMapped,
      }
    }
  }

  if (u === 'IFCPOLYLINE' || u === 'IFCINDEXEDPOLYCURVE') {
    const pts = Array.isArray(o.Points) ? o.Points.length : null
    return { type, expressID, detail: pts !== null ? `${pts} pontos` : '', isMapped }
  }

  return { type, expressID, detail: '', isMapped }
}

export interface RawElementInfo {
  expressID: number | null
  ifcType: string
  globalId: string | null
  name: string | null
  hasMappedRepresentation: boolean
  representations: RepresentationEntry[]
  placementChain: PlacementEntry[]
}

export function decodeRawElement(
  entity: unknown,
  placementChainRaw: unknown[],
): RawElementInfo {
  const o = asObj(entity)
  const expressID = o && typeof o.expressID === 'number' ? o.expressID : null
  const ifcType = (o ? getString(o.type) : null) ?? '?'
  const globalId = o ? getString(o.GlobalId) : null
  const name = o ? getString(o.Name) : null
  const representations = decodeRepresentation(o)
  const placementChain = placementChainRaw
    .map((p) => decodePlacement(asObj(p)))
    .filter((p): p is PlacementEntry => p !== null)
  const hasMappedRepresentation = representations.some(
    (r) =>
      r.type.toUpperCase().includes('MAPPED') ||
      r.items.some((it) => it.isMapped),
  )
  return {
    expressID,
    ifcType,
    globalId,
    name,
    representations,
    hasMappedRepresentation,
    placementChain,
  }
}
